/**
 * useEmailVerification Hook
 * Manages email verification state and resend functionality
 */

import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/clients/supabase';
import { useToast } from '../../../../shared/contexts/ToastContext';
import { useResendTimer } from './useResendTimer';
import type { EmailVerificationState, ResendTracking } from '../../types/email-verification';

interface UseEmailVerificationReturn {
  resendEmail: () => Promise<void>;
  isResending: boolean;
  canResend: boolean;
  cooldownSeconds: number;
  attempts: number;
  maxAttempts: number;
}

const COOLDOWN_DURATION = parseInt(import.meta.env.VITE_EMAIL_RESEND_COOLDOWN || '60');
const MAX_RESENDS_PER_HOUR = parseInt(import.meta.env.VITE_MAX_EMAIL_RESENDS || '3');
const MAX_RESENDS_PER_DAY = 10;

export const useEmailVerification = (email: string): UseEmailVerificationReturn => {
  const [isResending, setIsResending] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const { showSuccess, showError } = useToast();
  const { canResend: timerCanResend, cooldownSeconds, startCooldown } = useResendTimer(COOLDOWN_DURATION);

  // Load resend tracking from localStorage
  const getResendTracking = useCallback((): ResendTracking => {
    const today = new Date().toISOString().split('T')[0];
    const stored = localStorage.getItem(`email_resend_${email}`);
    
    if (stored) {
      const tracking: ResendTracking = JSON.parse(stored);
      
      // Reset daily count if it's a new day
      if (tracking.resetDate !== today) {
        tracking.dailyCount = 0;
        tracking.resetDate = today;
      }
      
      return tracking;
    }
    
    return {
      email,
      attempts: 0,
      lastAttempt: new Date(),
      dailyCount: 0,
      resetDate: today,
    };
  }, [email]);

  // Save resend tracking to localStorage
  const saveResendTracking = useCallback((tracking: ResendTracking) => {
    localStorage.setItem(`email_resend_${email}`, JSON.stringify(tracking));
  }, [email]);

  // Check if user can resend based on rate limits
  const canResendEmail = useCallback((): boolean => {
    const tracking = getResendTracking();
    const now = new Date();
    const lastAttempt = new Date(tracking.lastAttempt);
    const hoursSinceLastAttempt = (now.getTime() - lastAttempt.getTime()) / (1000 * 60 * 60);
    
    // Check daily limit
    if (tracking.dailyCount >= MAX_RESENDS_PER_DAY) {
      return false;
    }
    
    // Check hourly limit
    if (hoursSinceLastAttempt < 1 && tracking.attempts >= MAX_RESENDS_PER_HOUR) {
      return false;
    }
    
    return timerCanResend;
  }, [getResendTracking, timerCanResend]);

  // Load initial attempts count
  useEffect(() => {
    const tracking = getResendTracking();
    setAttempts(tracking.attempts);
  }, [getResendTracking]);

  const resendEmail = useCallback(async () => {
    if (!email || !canResendEmail()) {
      return;
    }

    setIsResending(true);
    
    try {
      // Use Supabase resend confirmation
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (error) {
        throw error;
      }

      // Update tracking
      const tracking = getResendTracking();
      tracking.attempts += 1;
      tracking.dailyCount += 1;
      tracking.lastAttempt = new Date();
      saveResendTracking(tracking);
      
      setAttempts(tracking.attempts);
      
      showSuccess('Email konfirmasi berhasil dikirim ulang! Silakan cek inbox Anda.');
      startCooldown();
      
    } catch (error: any) {
      console.error('Resend email error:', error);
      
      // Handle specific Supabase errors
      let errorMessage = 'Gagal mengirim ulang email. Silakan coba lagi.';
      
      if (error.message?.includes('rate limit')) {
        errorMessage = 'Terlalu banyak percobaan. Silakan tunggu beberapa saat.';
      } else if (error.message?.includes('already confirmed')) {
        errorMessage = 'Email sudah dikonfirmasi. Silakan login.';
      } else if (error.message?.includes('not found')) {
        errorMessage = 'Email tidak ditemukan. Silakan daftar ulang.';
      }
      
      showError(errorMessage);
    } finally {
      setIsResending(false);
    }
  }, [email, canResendEmail, showSuccess, showError, startCooldown, getResendTracking, saveResendTracking]);

  return {
    resendEmail,
    isResending,
    canResend: canResendEmail(),
    cooldownSeconds,
    attempts,
    maxAttempts: MAX_RESENDS_PER_HOUR,
  };
};